import { useLayoutEffect, useMemo } from 'react';
import { GeoJSON, useMap } from 'react-leaflet';
import type { PathOptions } from 'leaflet';
import { useIcasGeoJson } from './useIcasGeoJson';
import { entitatiPopupHtml } from './popups';
import { ICAS_PANE, ensureIcasPane } from './pane';
import type { IcasFeatureProperties } from './types';

type Props = {
  /**
   * Only entitati ships as static GeoJSON now — trupuri + stands are
   * served as PMTiles vector tiles via `IcasPmtilesLayer`. The 23-polygon
   * entitati layer stays GeoJSON because (a) it's small enough that
   * gzip + browser cache make the network cost free, and (b) SVG
   * renders entitati's bold dashed outlines more crisply than the
   * canvas-based vector-tile pipeline.
   */
  layer: 'entitati';
  enabled: boolean;
};

const ENTITATI_STYLE: PathOptions = {
  color: '#7c3aed', // violet-600 — bold border for the 23-zone overlay
  weight: 2,
  fillColor: '#a78bfa',
  fillOpacity: 0.08,
  dashArray: '6,4',
};

/**
 * SVG-rendered ICAS entitati overlay. Pinned to the dedicated low
 * z-index pane (`icasPane`) so user-managed parcel polygons + photo
 * markers stay on top regardless of toggle / attach order.
 */
export function IcasGeoJsonLayer({ layer, enabled }: Props) {
  const { data } = useIcasGeoJson(layer, enabled);
  const map = useMap();

  // `useLayoutEffect` — not `useEffect` — so the pane exists *before*
  // the child <GeoJSON pane={ICAS_PANE}> attaches in its own effect.
  // With useEffect, React commits the GeoJSON child first; Leaflet
  // tries to find the pane, gets `undefined`, and throws
  // "Cannot read properties of undefined (reading 'appendChild')".
  useLayoutEffect(() => {
    if (map) ensureIcasPane(map);
  }, [map]);

  const renderKey = useMemo(
    () => (data ? `${layer}-${data.features.length}` : null),
    [data, layer],
  );

  if (!enabled || !data || !renderKey) return null;

  return (
    <GeoJSON
      key={renderKey}
      data={data}
      style={() => ENTITATI_STYLE}
      pane={ICAS_PANE}
      onEachFeature={(feature, leafletLayer) => {
        const props = (feature.properties ?? {}) as IcasFeatureProperties;
        leafletLayer.bindPopup(entitatiPopupHtml(props), { maxWidth: 320 });
      }}
    />
  );
}
